import { z } from "zod/v4-mini";

import { cz } from "@/utils/zodUtils";

import { TandemEventDefinition } from "../TandemTypes";

export type BasalRateChangeType =
  | "timed_segment"
  | "new_profile"
  | "temp_rate_start"
  | "temp_rate_end"
  | "pump_suspended"
  | "pump_resumed"
  | "pump_shut_down"
  | "basal_limit";

const basalRateChangeFlags = {
  timed_segment: 0x01,
  new_profile: 0x02,
  temp_rate_start: 0x04,
  temp_rate_end: 0x08,
  pump_suspended: 0x10,
  pump_resumed: 0x20,
  pump_shut_down: 0x40,
  basal_limit: 0x80,
};

export type BasalRateChangePayload = {
  commanded_basal_rate: number;
  base_basal_rate: number;
  max_basal_rate: number;
  idp?: number;
  change_type?: number;
};

export type BasalRateChangeEvent = {
  commandedBasalRate: number;
  baseBasalRate: number;
  maxBasalRate: number;
  ratePercent: number;
  idp?: number;
  changeType?: number;
  changeTypes: BasalRateChangeType[];
};

const basalRateChangeSchema = z.object({
  commanded_basal_rate: z.number(),
  base_basal_rate: z.number(),
  max_basal_rate: z.number(),
  idp: cz.permissive(z.number()),
  change_type: cz.permissive(z.number()),
});

export const LID_BASAL_RATE_CHANGE = {
  value: 0x03,
  name: "Basal Rate Change Event",
  format: "fffhb.",
  fields: ["commanded_basal_rate", "base_basal_rate", "max_basal_rate", "idp", "change_type"],
  postprocess: rec => {
    return {
      commandedBasalRate: rec.commanded_basal_rate,
      baseBasalRate: rec.base_basal_rate,
      maxBasalRate: rec.max_basal_rate,
      ratePercent: rec.base_basal_rate > 0 ? rec.commanded_basal_rate / rec.base_basal_rate : 0,
      idp: rec.idp,
      changeType: rec.change_type,
      changeTypes: getChangeTypes(rec.change_type),
    };
  },
  schema: basalRateChangeSchema,
} as const satisfies TandemEventDefinition<BasalRateChangePayload, BasalRateChangeEvent>;

const getChangeTypes = (changeType?: number) => {
  if (!changeType) {
    return [];
  }
  const flags: BasalRateChangeType[] = [];
  for (const [key, value] of Object.entries(basalRateChangeFlags)) {
    if (value & changeType) {
      flags.push(key);
    }
  }
  return flags;
};
